// src/hooks/useSignUpForm.js
import { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAuthRedirect } from './useAuthRedirect';
import { API } from '../api/config';

const API_URL = API.LOGIN;
//const API_URL = "http://localhost:8080";
// Função para aplicar a máscara de telefone dinamicamente
const formatPhoneMask = (val) => {
    let value = val.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    if (value.length > 9) value = `${value.slice(0, 10)}-${value.slice(10)}`;
    return value;
};

export const useSignUpForm = () => {
    const navigate = useNavigate();
    const { handleRedirect } = useAuthRedirect();
    const { user, loading, login } = useContext(AuthContext); 
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- STATES DO FORMULÁRIO ---
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        birthdayDay: '',
        birthdayMonth: ''
    });

    // --- STATES DE VERIFICAÇÃO (NOVO) ---
    const [verificationStep, setVerificationStep] = useState(false);
    const [verificationPhone, setVerificationPhone] = useState("");
    const [verificationExpiresAt, setVerificationExpiresAt] = useState(null);
    const [verificationError, setVerificationError] = useState("");
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [inputCode, setInputCode] = useState(""); 

    // Inicialização e Session Recovery
    useEffect(() => {
        if (!loading && !user) {
            navigate('/login');
            return;
        }

        if (!loading && user) {
            setFormData(prev => ({
                ...prev,
                nome: user.name || user.Nome || "", 
                email: user.email || user.Email || ""
            }));

            // Tenta recuperar sessão de verificação
            const savedVerification = sessionStorage.getItem("phone_verification");
            if (savedVerification) {
                try {
                    const { telefone, expiresAt } = JSON.parse(savedVerification);
                    if (expiresAt && Date.now() < expiresAt) {
                        setVerificationStep(true);
                        setVerificationPhone(telefone);
                        setVerificationExpiresAt(expiresAt);
                        setFormData(prev => ({ ...prev, telefone: formatPhoneMask(telefone) }));
                    } else {
                        sessionStorage.removeItem("phone_verification");
                    }
                } catch (e) {
                    sessionStorage.removeItem("phone_verification");
                }
            }
        }
    }, [user, loading, navigate]);

    // Timer / Countdown Effect
    useEffect(() => {
        if (!verificationStep || !verificationExpiresAt) return;

        const tick = () => {
            const remaining = Math.max(0, verificationExpiresAt - Date.now());
            setTimeLeft(remaining);
        };

        tick(); 
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [verificationStep, verificationExpiresAt]);

    // Handlers de Input
    const handlePhoneChange = (e) => {
        setFormData(prev => ({ ...prev, telefone: formatPhoneMask(e.target.value) }));
        setVerificationError(""); 
    };

    const handleBirthdayDayChange = (e) => {
        setFormData(prev => ({ ...prev, birthdayDay: e.target.value }));
        setVerificationError("");
    };

    const handleBirthdayMonthChange = (e) => {
        setFormData(prev => ({ ...prev, birthdayMonth: e.target.value }));
        setVerificationError("");
    };

    const isPhoneValid = useMemo(() => {
        const cleanPhone = formData.telefone.replace(/\D/g, "");
        return cleanPhone.length === 11;
    }, [formData.telefone]);

    // --- STEP 1: SOLICITAR CÓDIGO VIA API ---
    const initiateVerification = async (e) => {
        if (e) e.preventDefault();
        setVerificationError("");

        if (!isPhoneValid) {
            setVerificationError("Insira um telefone válido com DDD (exatamente 11 dígitos).");
            return;
        }
        if (!formData.birthdayDay || !formData.birthdayMonth) {
            setVerificationError("Selecione o dia e mês do seu aniversário.");
            return;
        }

        const cleanPhone = formData.telefone.replace(/\D/g, "");
        setIsSendingCode(true);

        try {
            const response = await fetch(`${API_URL}/auth/phone/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ telefone: cleanPhone })
            });

            if (response.ok || response.status === 429) {
                const expiresAt = Date.now() + (5 * 60 * 1000);
                setVerificationStep(true);
                setVerificationPhone(cleanPhone);
                setVerificationExpiresAt(expiresAt);
                setInputCode("");
                
                sessionStorage.setItem("phone_verification", JSON.stringify({ 
                    telefone: cleanPhone, 
                    expiresAt 
                }));

            } else {
                // LÊ O ERRO EXATO DO BACKEND (Em vez de mensagem genérica)
                const errorData = await response.json().catch(() => ({}));
                setVerificationError(`Erro do Backend: ${errorData.error || response.statusText}`);
            }
        } catch (error) {
            setVerificationError("Falha na conexão com o servidor.");
        } finally {
            setIsSendingCode(false);
        }
    };

    // --- REENVIAR CÓDIGO ---
    const handleResendCode = async () => {
        if (timeLeft > 0) return; // Proteção extra
        setInputCode("");
        await initiateVerification();
    };

    // --- STEP 2: CONFIRMAR CÓDIGO E CADASTRAR ---
    const finalizeRegistration = async (e) => {
        e.preventDefault();
        setVerificationError("");

        if (!verificationPhone) {
            cancelVerification();
            return;
        }

        if (inputCode.length !== 3) {
            setVerificationError("O código deve conter 3 dígitos.");
            return;
        }

        setIsSubmitting(true);

        try {
            // 1. Confirma o Telefone
            const confirmRes = await fetch(`${API_URL}/auth/phone/confirm`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}` // <- Token adicionado
                },
                body: JSON.stringify({ 
                    telefone: verificationPhone, // Verifique se não precisa ser "Telefone"
                    codigo: inputCode // Verifique se não precisa ser "Codigo"
                })
            });

            if (!confirmRes.ok) {
                throw new Error("Código inválido ou expirado.");
            }

            const formattedMonth = String(formData.birthdayMonth).padStart(2, '0');
            const formattedDay = String(formData.birthdayDay).padStart(2, '0');

            // 2. Finaliza o Cadastro
            const payload = {
                Nome: formData.nome,
                Email: formData.email,
                Telefone: verificationPhone,
                Aniversario: `${formattedMonth}-${formattedDay}`
            };

            const response = await fetch(`${API_URL}/clients`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}` 
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Erro ao cadastrar cliente.");
            }

            const updatedUser = { 
                ...user, 
                isNewUser: false, 
                telefone: verificationPhone,
                reservas: [],
                assinatura: null
            };
            
            sessionStorage.removeItem("phone_verification");
            login(updatedUser); 
            handleRedirect(); 

        } catch (error) {
            console.error("Erro no cadastro:", error);
            setVerificationError(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelVerification = () => {
        setVerificationStep(false);
        setInputCode("");
        setVerificationError("");
        sessionStorage.removeItem("phone_verification");
    };

    return {
        formData,
        isLoading: loading,
        isSubmitting,
        isSendingCode,
        isPhoneValid,
        handlePhoneChange,
        handleBirthdayDayChange,
        handleBirthdayMonthChange,
        verificationStep,
        inputCode,
        setInputCode,
        initiateVerification,
        finalizeRegistration,
        cancelVerification,
        handleResendCode,
        verificationError,
        timeLeft
    };
};